import i18next from "i18next";
import { applyMiddleware, combineReducers, compose, createStore } from "redux";
import createDebounce from "redux-debounced";
import { withExtraArgument } from "redux-thunk";

import app from "./app/reducers";
import map from "./map/reducers";
import oidc from "./oidc/reducers";

import type { Action, UnknownAction } from "redux";
import type { ThunkAction, ThunkDispatch } from "redux-thunk";

import type { EnvState } from "../envConfig";

import type { AppActionType } from "./app/actions";
import type { MapActionType } from "./map/actions";
import type { OidcActionType } from "./oidc/actions";

export type ActionType = AppActionType | MapActionType | OidcActionType;

/** Extra argument injected into every thunk by `withExtraArgument`. */
export interface ThunkExtraArg {
  t: typeof i18next.t;
}

/**
 * Full redux state shape.
 *
 * The individual reducers are still untyped so we cannot rely on
 * `ReturnType<typeof rootReducer>`. `EnvState` is the hand-maintained
 * description of the shape and is used everywhere in the app.
 */
export type RootState = EnvState;

/**
 * Dispatch type that understands both plain action objects and thunk
 * functions (including the extra `{ t }` argument).
 */
export type AppDispatch = ThunkDispatch<
  RootState,
  ThunkExtraArg,
  Action<ActionType>
>;

/** Convenience alias for typing thunk action creators. */
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  ThunkExtraArg,
  Action<ActionType> & UnknownAction
>;

/** Convenience alias for typing plain action objects. */
export type AppAction = Action<ActionType> & UnknownAction;

const rootReducer = combineReducers({
  app,
  map,
  oidc,
});

const store = createStore(
  rootReducer,
  compose(
    applyMiddleware(createDebounce(), withExtraArgument({ t: i18next.t })),
  ),
);

/** Typed store exposing `AppDispatch` for `dispatch`. */
export type AppStore = { dispatch: AppDispatch } & Omit<
  typeof store,
  "dispatch"
>;

export default store as AppStore;
